import { toDateString, useField, useForm } from "@taskfortress/core"
import type { Task } from "@taskfortress/core/src/clientData/contentTypes"
import { TaskStatus } from "@taskfortress/core/src/clientData/contentTypes"
import { validateTaskTitle } from "@taskfortress/core/src/form/validation"
import React, { useCallback, useMemo } from "react"

interface TaskFormData {
  titleField: ReturnType<typeof useField<string>>
  descriptionField: ReturnType<typeof useField<string>>
  statusField: ReturnType<typeof useField<boolean>>
  dueDateField: ReturnType<typeof useField<Date>>
  tagsField: ReturnType<typeof useField<string[]>>
  form: ReturnType<typeof useForm>
  handleFormSubmit: (e?: React.FormEvent) => void
  isNewTask: boolean
  tagSuggestions: string[]
}

interface TaskFormProps {
  task: Task
  onSave: (task: Task) => void
  onClose: () => void
  tagSuggestions: string[]
  children: (data: TaskFormData) => React.ReactNode
}

export const TaskForm: React.FC<TaskFormProps> = ({
  task,
  onSave,
  tagSuggestions,
  children,
}) => {
  // Create form fields using useField
  const titleField = useField<string>({
    name: "title",
    initialValue: task?.title || "",
    validate: validateTaskTitle,
  })

  const descriptionField = useField<string>({
    name: "description",
    optional: true,
    initialValue: task?.description || "",
  })

  const statusField = useField<boolean>({
    name: "status",
    optional: true,
    initialValue: task?.status === TaskStatus.DONE,
  })

  const dueDateField = useField<Date>({
    name: "dueDate",
    optional: true,
    initialValue: task?.dueDate ? new Date(task.dueDate) : undefined,
  })

  const tagsField = useField<string[]>({
    name: "tags",
    optional: true,
    initialValue: task?.tags || [],
  })

  // Create form using useForm
  const form = useForm({
    fields: [
      titleField,
      descriptionField,
      statusField,
      dueDateField,
      tagsField,
    ],
  })

  const handleFormSubmit = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault()

      if (!form.valid) return

      const updatedTask: Task = {
        ...task,
        title: titleField.value || "",
        description: descriptionField.value || "",
        status: statusField.value ? TaskStatus.DONE : TaskStatus.TODO,
        dueDate: dueDateField.value
          ? toDateString(dueDateField.value)
          : undefined,
        tags: tagsField.value || [],
      }

      form.reset()
      onSave(updatedTask)
    },
    [
      task,
      form.valid,
      form.reset,
      titleField.value,
      descriptionField.value,
      statusField.value,
      dueDateField.value,
      tagsField.value,
      onSave,
    ],
  )

  const isNewTask = useMemo(() => !task?.id, [task?.id])

  const formData: TaskFormData = {
    titleField,
    descriptionField,
    statusField,
    dueDateField,
    tagsField,
    form,
    handleFormSubmit,
    isNewTask,
    tagSuggestions,
  }

  return children(formData)
}
